import React, { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { createAgentRequest } from '@pole-api/lib-core-agent-api/lib/clients/createAgentRequest'
import { useMutation } from '@tanstack/react-query'
import { AgentRegistrationCommunicationMethod } from '@/components/composed/financing/AgentRegistrationCommunicationMethod'
import { EAgentRegistrationCommunicationMethod } from '@/components/composed/financing/AgentRegistrationCommunicationMethod/constants'
import { EAgentRegistrationStep } from '@/types/EAgentRegistrationStep'
import type { IAgentRegistrationRequestFormValues } from '@/types/IAgentRegistrationRequestFormValues'
import { getUtmParameters } from '@/utils/utm/getUtmParameters'
import { PublicationFinancingWidgetCollapsingAgentRegistrationBaseForm } from './components/PublicationFinancingWidgetCollapsingAgentRegistrationBaseForm'
import { PublicationFinancingWidgetCollapsingAgentRegistrationError } from './components/PublicationFinancingWidgetCollapsingAgentRegistrationError'
import { PublicationFinancingWidgetCollapsingAgentRegistrationSuccess } from './components/PublicationFinancingWidgetCollapsingAgentRegistrationSuccess'
import './styles.scss'

export const PublicationFinancingWidgetCollapsingAgentRegistration: React.FC = () => {
  const [registrationStep, setRegistrationStep] = useState<EAgentRegistrationStep>(EAgentRegistrationStep.BASE_FORM)

  const { ...formMethods } = useForm<IAgentRegistrationRequestFormValues>({
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      regions: [],
      communicationMethod: EAgentRegistrationCommunicationMethod.CALL_PHONE,
    },
  })

  const { mutateAsync: createRegistrationAgentRequest, isLoading } = useMutation({
    mutationFn: createAgentRequest,
    onSuccess: () => {
      setRegistrationStep(EAgentRegistrationStep.SUCCESS)
      formMethods.reset()
    },
    onError: () => setRegistrationStep(EAgentRegistrationStep.ERROR),
  })

  const goToCommunicationMethodStep = useCallback(() => setRegistrationStep(EAgentRegistrationStep.COMMUNICATION_METHOD), [])
  const goToBaseFormStep = useCallback(() => setRegistrationStep(EAgentRegistrationStep.BASE_FORM), [])

  const submitFormRegistrationAgent = async () => {
    const formValues = formMethods.getValues()
    const utmParameters = getUtmParameters()

    await createRegistrationAgentRequest({
      name: formValues.name,
      communicationMethod: formValues.communicationMethod,
      phone: formValues.phone,
      email: formValues.email,
      regions: formValues.regions,
      utmParams: {
        utmCampaign: utmParameters.utmCampaign ?? null,
        utmContent: utmParameters.utmContent ?? null,
        utmMedium: utmParameters.utmMedium ?? null,
        utmSource: utmParameters.utmSource ?? null,
        utmTerm: utmParameters.utmTerm ?? null,
      },
    })
  }

  const renderFormStep = () => {
    switch (registrationStep) {
      case EAgentRegistrationStep.BASE_FORM:
        return <PublicationFinancingWidgetCollapsingAgentRegistrationBaseForm onSubmit={goToCommunicationMethodStep} />
      case EAgentRegistrationStep.COMMUNICATION_METHOD:
        return <AgentRegistrationCommunicationMethod onSubmit={submitFormRegistrationAgent} isLoading={isLoading} />
      case EAgentRegistrationStep.SUCCESS:
        return <PublicationFinancingWidgetCollapsingAgentRegistrationSuccess onClose={goToBaseFormStep} />
      case EAgentRegistrationStep.ERROR:
        return <PublicationFinancingWidgetCollapsingAgentRegistrationError onClose={goToBaseFormStep} />
      default:
    }
  }

  return (
    <div className="publicationFinancingWidgetCollapsingAgentRegistration">
      <FormProvider {...formMethods}>{renderFormStep()}</FormProvider>
    </div>
  )
}
